import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

// Create axios instance with default config
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
adminApi.interceptors.request.use(
  (config) => {
    // BYPASS: Always add a dummy bypass token
    config.headers.Authorization = `Bearer bypass-token`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Admin Authentication
export const adminAuth = {
  login: async (credentials) => {
    const response = await adminApi.post('/admin/login', credentials);
    if (response.data.access_token) {
      localStorage.setItem('adminToken', response.data.access_token);
    }
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('adminToken');
  },
  
  verify: async () => {
    const response = await adminApi.get('/admin/verify');
    return response.data;
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('adminToken');
  }
};

// Contest Management
export const contestApi = {
  getAll: async () => {
    const response = await adminApi.get('/admin/contests');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await adminApi.get(`/admin/contests/${id}`);
    return response.data;
  },
  
  create: async (contestData) => {
    const response = await adminApi.post('/admin/contests', contestData);
    return response.data;
  },
  
  update: async (id, contestData) => {
    const response = await adminApi.put(`/admin/contests/${id}`, contestData);
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await adminApi.patch(`/admin/contests/${id}/status`, null, {
      params: { status }
    });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await adminApi.delete(`/admin/contests/${id}`);
    return response.data;
  }
};

// Payment Management
export const paymentApi = {
  getAll: async () => {
    const response = await adminApi.get('/admin/payments');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await adminApi.get(`/admin/payments/${id}`);
    return response.data;
  },
  
  createOrder: async (orderData) => {
    const response = await adminApi.post('/admin/payments/order', orderData);
    return response.data;
  },
  
  verify: async (paymentId, verificationData) => {
    const response = await adminApi.post(`/admin/payments/${paymentId}/verify`, verificationData);
    return response.data;
  }
};

// Refund Management
export const refundApi = {
  getAll: async () => {
    const response = await adminApi.get('/admin/refunds');
    return response.data;
  },
  
  create: async (refundData) => {
    const response = await adminApi.post('/admin/refunds', refundData);
    return response.data;
  }
};

// Winner Management
export const winnerApi = {
  getAll: async () => {
    const response = await adminApi.get('/admin/winners');
    return response.data;
  },
  
  getByContest: async (contestId) => {
    const response = await adminApi.get(`/admin/winners/contest/${contestId}`);
    return response.data;
  },
  
  create: async (winnerData) => {
    const response = await adminApi.post('/admin/winners', winnerData);
    return response.data;
  },
  
  updateStatus: async (winnerId, status) => {
    const response = await adminApi.patch(`/admin/winners/${winnerId}/status`, null, {
      params: { status }
    });
    return response.data;
  }
};

// Dashboard Metrics
export const dashboardApi = {
  getMetrics: async () => {
    const response = await adminApi.get('/admin/dashboard/metrics');
    return response.data;
  }
};

export default adminApi;